import { makeStyles, shorthands } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";

/**
 * German license-plate visual that wraps a real <input>. The plate frame
 * (blue EU strip + 12-star ring + yellow body) is purely decorative;
 * the underlying input is fully a11y/keyboard accessible.
 */

const useStyles = makeStyles({
  wrap: {
    display: "inline-flex",
    alignItems: "stretch",
    width: "100%",
    maxWidth: "440px",
    ...shorthands.border("3px", "solid", "#1a1a1a"),
    ...shorthands.borderRadius("8px"),
    backgroundColor: "#FCDB1F", // bright yellow rear-plate
    boxShadow: "0 1px 0 rgba(0,0,0,0.15), inset 0 0 0 1px rgba(0,0,0,0.05)",
    fontFamily: BRAND.fontPrimary,
    overflow: "hidden",
  },
  euStrip: {
    width: "44px",
    backgroundColor: "#003399", // EU blue
    color: BRAND.white,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "space-between",
    paddingTop: "8px",
    paddingBottom: "6px",
  },
  countryLetter: {
    fontFamily: "'Courier New', monospace",
    fontWeight: 700,
    fontSize: "0.95rem",
    letterSpacing: "0.02em",
    color: BRAND.white,
  },
  inputWrap: {
    flexGrow: 1,
    display: "flex",
    alignItems: "center",
    paddingLeft: "12px",
    paddingRight: "12px",
  },
  input: {
    width: "100%",
    backgroundColor: "transparent",
    backgroundImage: "none",
    color: "#1a1a1a",
    fontFamily:
      "'EuroPlate', 'Helvetica Neue', 'Arial Black', 'Arial', sans-serif",
    fontWeight: 800,
    letterSpacing: "0.04em",
    fontSize: "1.6rem",
    paddingTop: "8px",
    paddingBottom: "8px",
    textTransform: "uppercase",
    outlineWidth: 0,
    ...shorthands.border("0"),
    "::placeholder": {
      color: "rgba(0,0,0,0.35)",
      fontWeight: 700,
      letterSpacing: "0.02em",
    },
  },
});

const StarRing = () => (
  <svg width="22" height="22" viewBox="-12 -12 24 24" aria-hidden="true">
    {Array.from({ length: 12 }).map((_, i) => {
      const angle = (i / 12) * Math.PI * 2 - Math.PI / 2;
      const x = Math.cos(angle) * 9;
      const y = Math.sin(angle) * 9;
      return <circle key={i} cx={x} cy={y} r="1.1" fill="#FFCC00" />;
    })}
  </svg>
);

type Props = {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
};

const LicensePlateInput = ({ value, onChange, placeholder }: Props) => {
  const styles = useStyles();
  return (
    <div className={styles.wrap}>
      <div className={styles.euStrip} aria-hidden="true">
        <StarRing />
        <span className={styles.countryLetter}>D</span>
      </div>
      <div className={styles.inputWrap}>
        <input
          className={styles.input}
          type="text"
          value={value}
          placeholder={placeholder ?? "AC-XP 123"}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          spellCheck={false}
          autoCapitalize="characters"
          autoCorrect="off"
          aria-label="Kennzeichen"
        />
      </div>
    </div>
  );
};

export default LicensePlateInput;
